import { FC, useMemo } from 'react';
import { StatusBadge } from '../StatusBadge/StatusBadge';
import { Tag } from '../Tag/Tag';
import { useIntl } from 'react-intl';
import styles from './ReportItem.module.scss';
import { formatDateTime } from '../../utils/dateUtils';
import { ReportStatus } from '../../types';
import { TrafficLightPhoto } from '../ProfilePhotoStack/ProfilePhotoStack';

interface ReportItemProps {
  filename: string;
  status: ReportStatus;
  details?: {
    profile_photos?: TrafficLightPhoto[];
    photo_data?: any;
    candidatereport_data?: {
      candidateFirstName: string;
      candidateLastName: string;
      profilePhotoFileName?: string;
      companyApplied?: string;
      jobNumber?: string;
    };
    processed?: string;
    consultingTeam?: Array<{ name: string }>;
    lastModified?: string;
    candidate_name?: string;
    currentStep?: number;
    warnings?: Array<{ name: string }> | string;
  };
  progress?: string;
  role?: string;
  remarks?: string;
  showProfilePhoto?: boolean;
  imageComponent?: React.ReactNode;
}

export const ReportItem: FC<ReportItemProps> = ({
  filename,
  status,
  details,
  progress,
  role,
  remarks,
  showProfilePhoto = false,
  imageComponent,
}) => {
  const intl = useIntl();

  const displayDate = details?.processed || details?.lastModified;
  const displayName = details?.candidate_name || details?.candidatereport_data?.candidateFirstName + ' ' + details?.candidatereport_data?.candidateLastName || filename;

  // Parse warnings if they exist and are in string format
  const parsedWarnings = useMemo(() => {
    if (!details?.warnings) return [];

    // If warnings is already an array of objects, return it
    if (Array.isArray(details.warnings)) {
      return details.warnings;
    }

    // If warnings is a string, try to parse it as JSON
    try {
      const parsedJson = JSON.parse(details.warnings);
      // Transform string array into array of objects with name property
      if (Array.isArray(parsedJson)) {
        return parsedJson.map(warning => ({ name: warning }));
      }
      return [];
    } catch (error) {
      console.error('Failed to parse warnings:', error);
      return [];
    }
  }, [details?.warnings]);

  return (
    <div className={styles.listItem}>
      {imageComponent}
      <div className={styles.itemContent}>
        <div className={styles.itemHeader}>
          <div className={styles.itemNameContainer}>
            <div className={styles.itemName}>{displayName}</div>
            {(details?.candidatereport_data?.companyApplied || details?.candidatereport_data?.jobNumber) && (
              <div className={styles.teamContainer} style={{ marginTop: '0.5rem' }}>
                {details?.candidatereport_data?.companyApplied && (
                  <Tag className={styles.teamChip}>{details?.candidatereport_data?.companyApplied}</Tag>
                )}
                {details?.candidatereport_data?.jobNumber && (
                  <Tag className={styles.teamChip}>{details?.candidatereport_data?.jobNumber}</Tag>
                )}

                {parsedWarnings.length > 0 && (
                  <div className={styles.warningsContainer}>
                    <div className={styles.teamContainer} style={{ marginLeft: '0.5rem' }}>
                      {parsedWarnings.map((warning, index) => (
                        <Tag key={`${warning.name}-${index}`} className={styles.warningChip}>
                          {warning.name}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <StatusBadge status={status} />
        </div>
        <div className={styles.itemDetails}>
          {details?.consultingTeam && details.consultingTeam.length > 0 && (
            <div className={styles.teamContainer}>
              {details.consultingTeam.map((member, index) => (
                <Tag key={`${member.name}-${index}`} className={styles.teamChip}>
                  {member.name}
                </Tag>
              ))}
            </div>
          )}
          {role && (
            <>
              <span>
                {intl.formatMessage(
                  { id: 'reportItem.label.role' },
                  { 
                    role: (() => {
                      try {
                        return decodeURIComponent(role);
                      } catch (e) {
                        console.error('Failed to decode role:', e);
                        return role; // Fallback to original value if decoding fails
                      }
                    })()
                  }
                )}
              </span>
              <span className={styles.dotSeparator} />
            </>
          )}
          {remarks && (
            <>
              <span>{remarks}</span>
              <span className={styles.dotSeparator} />
            </>
          )}
          {displayDate && (
            <span>
              {intl.formatMessage(
                { id: 'common.metadata.processedDate' },
                {
                  date: formatDateTime(displayDate).toLocaleString(),
                },
              )}
            </span>
          )}
          {status === 'Processing' && progress && (
            <span>{intl.formatMessage({ id: 'common.metadata.progress' }, { progress })}</span>
          )}
        </div>
      </div>
    </div>
  );
};
